import React, { useState } from 'react';
import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { renderToStaticMarkup } from 'react-dom/server';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { useAuth0 } from "@auth0/auth0-react";
import { InvaderService } from "../services/InvaderService";
import { InvaderStatus } from "../constants/InvaderStatus";

const CustomMarker = ({ invader, onCapture, onUncapture, onStatusChange }) => {
    // Vert = capturé ; rouge = détruit uniquement ; bleu sinon (OK ou abimé)
    const color = invader.isCaptured()
        ? '#008000'
        : (invader.status === InvaderStatus.DESTROYED ? '#ef4444' : '#3333ff');
    const { getAccessTokenSilently } = useAuth0();
    const [showModal, setShowModal] = useState(false);
    const [points, setPoints] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [isUncapturing, setIsUncapturing] = useState(false);

    const handleCameraClick = () => {
        if (invader.needsValue()) {
            setShowModal(true);
            setPoints('');
            setError('');
        } else {
            handleMarkAsTook(invader.id);
        }
    };

    const handleCloseModal = () => {
        if (!isSubmitting) {
            setShowModal(false);
            setPoints('');
            setError('');
        }
    };

    const handleSubmitPoints = async () => {
        const pointsValue = parseInt(points, 10);
        if (isNaN(pointsValue) || pointsValue < 10 || pointsValue > 100) {
            setError('Les points doivent être entre 10 et 100');
            return;
        }

        setIsSubmitting(true);
        setError('');
        try {
            await InvaderService.updateValue(getAccessTokenSilently, invader.id, pointsValue);
            await InvaderService.markAsTook(getAccessTokenSilently, invader.id);
            console.log("Marked as took with points:", pointsValue);
            setShowModal(false);
            setPoints('');
            onCapture(invader.id);
        } catch (error) {
            console.error("Erreur lors de la requête:", error);
            setError('Une erreur est survenue. Veuillez réessayer.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleMarkAsTook = async (id) => {
        try {
            await InvaderService.markAsTook(getAccessTokenSilently, id);
            console.log("Marked as took");
            onCapture(id);
        } catch (error) {
            console.error("Erreur lors de la requête:", error);
        }
    };

    const handleStatusChange = async (newStatus) => {
        if (newStatus === invader.status) return;

        setIsUpdatingStatus(true);
        try {
            await InvaderService.updateStatus(getAccessTokenSilently, invader.id, newStatus);
            if (onStatusChange) {
                onStatusChange(invader.id, newStatus);
            }
        } catch (error) {
            console.error("Erreur lors de la mise à jour du statut:", error);
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const handleUncaptureClick = async () => {
        setIsUncapturing(true);
        try {
            await InvaderService.removeInvader(getAccessTokenSilently, invader.id);
            console.log("Invader uncaptured");
            if (onUncapture) {
                onUncapture(invader.id);
            }
        } catch (error) {
            console.error("Erreur lors du déflash:", error);
        } finally {
            setIsUncapturing(false);
        }
    };

    const statusButtons = [
        { status: InvaderStatus.OK, label: 'OK', icon: '✓', color: '#22c55e', bgColor: '#22c55e20' },
        { status: InvaderStatus.DAMAGED, label: 'Abimé', icon: '⚠', color: '#f59e0b', bgColor: '#f59e0b20' },
        { status: InvaderStatus.DESTROYED, label: 'Détruit', icon: '✗', color: '#ef4444', bgColor: '#ef444420' },
    ];

    // Convertir l'icône Material-UI en HTML statique
    const iconMarkup = renderToStaticMarkup(
        <svg xmlns="http://www.w3.org/2000/svg" fill={color} viewBox="0 0 24 24" width="40px" height="40px">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 4.25 6 11 6 11s6-6.75 6-11c0-3.87-3.13-7-6-7z"/>
        </svg>
    );

    // Créer une icône Leaflet personnalisée à partir du HTML généré
    const customIcon = L.divIcon({
        html: iconMarkup,
        className: 'custom-div-icon', // Utilisez cette classe pour des styles supplémentaires
        iconSize: [40, 40],
        iconAnchor: [20, 40], // Point d'ancrage
        popupAnchor: [0, -40], // Position du popup
    });

    return (
        <>
            <Marker position={invader.position} icon={customIcon}>
                <Popup>
                    <div style={{ minWidth: '200px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                            <h3 style={{ margin: 0, marginRight: '10px', color: '#000', fontSize: '18px', fontWeight: 'bold' }}>{invader.id}</h3>
                            {invader.canBeUncaptured() ? (
                                <HighlightOffIcon
                                    style={{
                                        cursor: isUncapturing ? 'wait' : 'pointer',
                                        color: '#ef4444',
                                        fontSize: '30px',
                                        transition: 'transform 0.2s',
                                        opacity: isUncapturing ? 0.6 : 1,
                                    }}
                                    onClick={!isUncapturing ? handleUncaptureClick : undefined}
                                    onMouseOver={(e) => !isUncapturing && (e.currentTarget.style.transform = 'scale(1.2)')}
                                    onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                                />
                            ) : !invader.isCaptured() ? (
                                <CameraAltIcon
                                    style={{
                                        cursor: 'pointer',
                                        color: 'black',
                                        fontSize: '30px',
                                        transition: 'transform 0.2s',
                                    }}
                                    onClick={handleCameraClick}
                                    onMouseOver={(e) => (e.currentTarget.style.transform = 'scale(1.2)')}
                                    onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                                />
                            ) : null}
                        </div>
                        {invader.imageUrl && (
                            <img
                                src={invader.imageUrl}
                                alt={invader.id}
                                loading="lazy"
                                style={{
                                    width: '100%',
                                    borderRadius: '8px',
                                    marginBottom: '10px',
                                    display: 'block',
                                }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        )}
                        {invader.value > 0 && (
                            <div style={{ color: '#b8860b', fontWeight: 'bold', marginBottom: '10px' }}>
                                {invader.value} points
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: '6px' }}>
                            {statusButtons.map(({ status, label, icon, color, bgColor }) => {
                                const isActive = invader.status === status;
                                return (
                                    <button
                                        key={status}
                                        onClick={() => handleStatusChange(status)}
                                        disabled={isUpdatingStatus}
                                        style={{
                                            flex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: '2px',
                                            padding: '8px 4px',
                                            border: isActive ? `2px solid ${color}` : '2px solid transparent',
                                            borderRadius: '8px',
                                            backgroundColor: isActive ? bgColor : '#f5f5f5',
                                            cursor: isUpdatingStatus ? 'wait' : 'pointer',
                                            opacity: isUpdatingStatus ? 0.6 : 1,
                                            transition: 'all 0.2s',
                                        }}
                                    >
                                        <span style={{ fontSize: '16px', color: color }}>{icon}</span>
                                        <span style={{ fontSize: '11px', color: isActive ? color : '#666', fontWeight: isActive ? 'bold' : 'normal' }}>{label}</span>
                                    </button>
                                );
                            })}
                        </div>
                        {isUpdatingStatus && (
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
                                <CircularProgress size={16} />
                            </div>
                        )}
                    </div>
                </Popup>
            </Marker>

            <Dialog
                open={showModal}
                onClose={handleCloseModal}
                disableEscapeKeyDown={isSubmitting}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{
                    backgroundColor: '#1a1a2e',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <span role="img" aria-label="invader">👾</span>
                    {invader.id}
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <p style={{ marginBottom: '16px', color: '#333' }}>
                        Pouvez-vous renseigner le nombre de points de ce Space Invader ?
                    </p>
                    <TextField
                        autoFocus
                        fullWidth
                        type="number"
                        label="Points"
                        placeholder="Ex: 10, 20, 50, 100..."
                        value={points}
                        onChange={(e) => setPoints(e.target.value)}
                        disabled={isSubmitting}
                        error={!!error}
                        helperText={error || "Valeur entre 10 et 100"}
                        inputProps={{ min: 10, max: 100, step: 10 }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !isSubmitting) {
                                handleSubmitPoints();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ padding: '16px 24px' }}>
                    <Button
                        onClick={handleCloseModal}
                        disabled={isSubmitting}
                        color="inherit"
                    >
                        Annuler
                    </Button>
                    <Button
                        onClick={handleSubmitPoints}
                        disabled={isSubmitting || !points}
                        variant="contained"
                        sx={{
                            backgroundColor: '#00d4ff',
                            '&:hover': { backgroundColor: '#00b8e6' },
                            minWidth: '120px'
                        }}
                    >
                        {isSubmitting ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'Valider'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CustomMarker;
